<template>
  <div class="property-tab">
    <div class="appearance-grid">
      <span class="appearance-label">Router:</span>
      <v-select v-model="router" :items="ROUTERS" density="compact" variant="outlined" hide-details class="field-bordered" :disabled="PreferenceStore.INSTANCE.isReadOnly"></v-select>
      <span class="appearance-label">Connector:</span>
      <v-select v-model="connector" :items="connectors" density="compact" variant="outlined" hide-details class="field-bordered" :disabled="PreferenceStore.INSTANCE.isReadOnly"></v-select>
      <span class="appearance-label">Line Color:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn class="button-transition-line-color" size="small" :color="lineColor" v-bind="{ ...tooltip, ...menu }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Line Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="lineColor" class="overflow-x-hidden" elevation="15" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
      </div>
      <span class="appearance-label">Show:</span>
      <v-btn-toggle v-model="showSpecification" mandatory density="compact" class="flex-toggle" color="font">
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn :value="true" size="x-small" class="flex-btn" :disabled="PreferenceStore.INSTANCE.isReadOnly" v-bind="props">
              <v-icon>mdi-code-braces</v-icon>
            </v-btn>
          </template>
          <span>Expression</span>
        </v-tooltip>
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn :value="false" size="x-small" class="flex-btn" :disabled="PreferenceStore.INSTANCE.isReadOnly" v-bind="props">
              <v-icon>mdi-text-box</v-icon>
            </v-btn>
          </template>
          <span>Docs</span>
        </v-tooltip>
      </v-btn-toggle>
    </div>
    <div class="appearance-footer">
      <v-btn size="small" color="primary" :disabled="PreferenceStore.INSTANCE.isReadOnly" @click="resetDefaults()">Reset Defaults</v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { POSSIBLE_ROUTING_STRATEGIES, Transition } from "../mvc/transition";
import RappidContext from "../rappid/rappid-context";
import { inject, computed, onBeforeUnmount, ref } from "vue";
import { PreferenceStore } from "../services/preference-store";

type RouterType = "normal" | "orthogonal" | "manhattan" | "metro" | "smart (obstacle-avoid)";
const ROUTERS: ReadonlyArray<RouterType> = POSSIBLE_ROUTING_STRATEGIES.slice(0, -1) as ReadonlyArray<RouterType>;
const connectors: Array<string> = ["rounded", "normal", "smooth", "jumpover"];
const colorPicking = ref<boolean>(false);

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const compProps = defineProps<{
  model: Transition;
}>();

const emit = defineEmits(["notifyChange"]);

onBeforeUnmount(() => {
  if (colorPicking.value) {
    rappidContext.commandManager.storeBatchCommand();
  }
});

function colorPickerHandler(value: boolean) {
  colorPicking.value = value;
  if (value) {
    startBatchCommand();
  } else {
    stopBatchCommand();
  }
}

function startBatchCommand() {
  rappidContext.commandManager.initBatchCommand();
}

function stopBatchCommand() {
  rappidContext.commandManager.storeBatchCommand();
}

function resetDefaults() {
  startBatchCommand();
  compProps.model.resetDefaults();
  stopBatchCommand();
}

const showSpecification = computed({
  get() {
    return compProps.model.getShowSpecification();
  },
  set(value: boolean) {
    startBatchCommand();
    compProps.model.setShowSpecification(value);
    emit("notifyChange", compProps.model);
    stopBatchCommand();
  },
});

const router = computed<RouterType>({
  get(): RouterType {
    const r = compProps.model.get("router");
    if (!r) {
      return "smart (obstacle-avoid)";
    }
    return r.name as RouterType;
  },
  set(name: RouterType) {
    if (name === "smart (obstacle-avoid)") {
      compProps.model.set("router", null);
    } else {
      compProps.model.router(name);
    }
  },
});

const connector = computed({
  get(): string {
    return "" + compProps.model.get("connector").name;
  },
  set(name: string) {
    compProps.model.connector(name);
  },
});

const lineColor = computed({
  get(): string {
    return compProps.model.getLineColor();
  },
  set(color: string) {
    compProps.model.setLineColor(color);
  },
});
</script>

<style>
.full-toggle {
  display: flex;
  width: 100%;
}

.half-btn {
  flex: 1;
  text-transform: none;
  font-size: 0.75rem !important;
  min-height: 28px !important;
  padding: 4px 8px !important;
}
</style>
